import { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options',
  className = '',
  disabled = false,
}: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };
  const toggleOption = (optionId: string) => {
    let newSelectedValues: string[] = [];

    if (optionId === 'All of the above') {
      // Select all except "All of the above" itself
      newSelectedValues = options
        .filter((option) => option.value !== 'All of the above')
        .map((option) => option.value);
    } else {
      // Toggle the selected option
      if (selectedValues.includes(optionId)) {
        newSelectedValues = selectedValues.filter((id) => id !== optionId);
      } else {
        newSelectedValues = [...selectedValues, optionId];
      }
      // If "All of the above" was selected, remove it
      newSelectedValues = newSelectedValues.filter((id) => id !== 'All of the above');
    }

    onChange(newSelectedValues);
  };

  const getSelectedLabels = () => {
    if (selectedValues.length === 0) return placeholder;

    return options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Toggle dropdown on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown();
    }
    // Close dropdown on Escape
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, optionId: string) => {
    // Select option on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOption(optionId);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-disabled={disabled}
        className={`flex justify-between items-center w-full py-3 px-4 text-lg border border-therapistQuestionsBorder bg-background rounded-md shadow-sm ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
        }`}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
      >
        <div className='truncate'>{getSelectedLabels()}</div>
        <div className='ml-2'>
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M19 9l-7 7-7-7'
            ></path>
          </svg>
        </div>
      </div>

      {isOpen && (
        <div
          className='absolute z-10 w-full mt-1 border border-therapistQuestionsBorder bg-background rounded-md shadow-lg max-h-60 overflow-y-auto'
          role='listbox'
          aria-multiselectable='true'
        >
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <div
                key={option.value}
                role='option'
                aria-selected={isSelected}
                tabIndex={0}
                className={`px-4 py-2 cursor-pointer flex items-center ${
                  isSelected ? 'bg-purple-100 text-primary font-medium' : 'hover:bg-gray-50'
                }`}
                onClick={() => toggleOption(option.value)}
                onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              >
                {isSelected && (
                  <svg
                    className='w-4 h-4 mr-2 text-primary'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden='true'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                )}
                <span className={isSelected ? 'ml-0' : 'ml-6'}>{option.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
